// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { Component } from "react";
import { CommandBar, CommandBarButton } from "office-ui-fabric-react/lib/";
import { withTranslation } from "react-i18next";

import { ConfigurationFormComponent } from "../ConfigurationFormComponent/ConfigurationFormComponent";
import { PreferencesFormComponent } from "../PreferencesFormComponent/PreferencesFormComponent";
import { configService } from "../../services/ConfigService";
import { eventService } from "../../services/EventService";
import DeleteAllTwinsComponent from "./DeleteAllTwinsComponent/DeleteAllTwinsComponent";

import "./AppCommandBar.scss";
import { KeyboardShortcutsComponents } from "../KeyboardShortcutsComponents/KeyboardShortcutsComponents";

class AppCommandBar extends Component {

  constructor(props) {
    super(props);
    this.delete = React.createRef();
    this.configure = React.createRef();
    this.preferences = React.createRef();
    this.keyboard = React.createRef();
    this.state = {
      isConnectionSettingsModalShowing: false
    };
  }

  componentDidMount() {
    eventService.subscribeConfigure(evt => {
      if (evt.type === "start") {
        this.setState({ isConnectionSettingsModalShowing: true });
      } else if (evt.type === "end") {
        this.setState({ isConnectionSettingsModalShowing: false });
      }
    });
  }

  updateAdtUrlSettings = async () => {
    try {
      const { appAdtUrl } = await configService.getConfig();
      this.configure.current.loadConfigurationSettings({ type: "start", appAdtUrl });
    } catch (exc) {
      if (exc.errorCode !== "user_cancelled") {
        exc.customMessage = "Error on saving settings";
        eventService.publishError(exc);
      }
    }
  }

  togglePreferencesModal = () => {
    this.preferences.current.loadExistingSettings();
  }

  // eslint-disable-next-line max-lines-per-function
  getFarItems = () => {
    const userSettingsButtonStyles = {
      root: {
        width: "auto !important",
        paddingLeft: "0 !important",
        paddingRight: "12px"
      },
      flexContainer: {
        flexDirection: "row-reverse",
        borderLeft: "1px solid #666"
      },
      label: {
        maxWidth: "150px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        padding: "0 8px",
        fontWeight: "400 !important",
        height: "15px"
      }
    };

    const environmentPickerButtonStyles = {
      root: {
        height: "48px",
        marginTop: "-2px",
        backgroundColor: "#3c3c41",
        padding: "0px 12px"
      },
      rootHovered: {
        backgroundColor: "#222222"
      },
      flexContainer: {
        flexDirection: "row-reverse"
      }
    };

    return [
      {
        key: "signIn",
        text: this.props.t("appCommandBar.signIn.text"),
        ariaLabel: this.props.t("appCommandBar.signIn.ariaLabel"),
        iconOnly: true,
        tooltipHostProps: {
          calloutProps: {
            hidden: this.state.isConnectionSettingsModalShowing
          }
        },
        commandBarButtonAs: () => (<CommandBarButton
          onClick={() => this.updateAdtUrlSettings()}
          iconProps={{
            iconName: "GlobeFavorite",
            styles: { root: { paddingLeft: "12px", color: "white" } },
            className: "app-commandbar-environment-picker-icon"
          }}
          className="app-commandbar-environment-picker"
          styles={environmentPickerButtonStyles}>
          <div>{(this.props.appAdtUrl.split(".")[0].split("//")[1])}</div>
        </CommandBarButton>)
      },
      {
        key: "keyboardShortcuts",
        text: this.props.t("appCommandBar.keyboardShortcuts.text"),
        ariaLabel: this.props.t("appCommandBar.keyboardShortcuts.ariaLabel"),
        iconProps: { iconName: "KeyboardClassic" },
        onClick: () => this.keyboard.current.open(),
        iconOnly: true,
        className: "app-toolbarButtons"
      },
      {
        key: "deleteTwins",
        text: this.props.t("appCommandBar.deleteTwins.text"),
        ariaLabel: this.props.t("appCommandBar.deleteTwins.ariaLabel"),
        iconProps: { iconName: "Delete" },
        onClick: () => this.delete.current.open(),
        iconOnly: true,
        className: "app-toolbarButtons delete-button"
      },
      {
        key: "settings",
        text: this.props.t("appCommandBar.settings.text"),
        ariaLabel: this.props.t("appCommandBar.settings.ariaLabel"),
        iconOnly: true,
        iconProps: { iconName: "Settings" },
        onClick: () => this.togglePreferencesModal(),
        className: "app-toolbarButtons settings-button"
      },
      {
        key: "showFRE",
        text: this.props.t("appCommandBar.helpContent.text"),
        ariaLabel: this.props.t("appCommandBar.helpContent.ariaLabel"),
        iconOnly: true,
        iconProps: { iconName: "StatusCircleQuestionMark" },
        onClick: () => this.props.showFRE(),
        className: "app-toolbarButtons settings-button question-mark-button"
      },
      {
        key: "feedback",
        text: this.props.t("appCommandBar.feedback.text"),
        ariaLabel: this.props.t("appCommandBar.feedback.ariaLabel"),
        iconOnly: true,
        iconProps: { iconName: "Emoji2" },
        onClick: () => {
          window.OfficeBrowserFeedback.multiFeedback()
            // eslint-disable-next-line no-console
            .catch(error => console.error("Multi feedback failed: ", error));
        },
        className: "app-toolbarButtons"
      },
      {
        key: "userSettings",
        text: this.props.userName,
        ariaLabel: this.props.t("appCommandBar.userSettings.ariaLabel"),
        iconOnly: false,
        iconProps: { iconName: "PlayerSettings" },
        onClick: () => this.props.onToggleUserMenu(),
        className: "app-toolbarButtons settings-button",
        buttonStyles: userSettingsButtonStyles
      }
    ];
  }

  render() {
    const farItems = this.getFarItems();
    return (
      <div className="app-commandbar-container">
        <CommandBar
          farItems={farItems}
          ariaLabel={this.props.t("appCommandBar.commandBar.ariaLabel")}
          className="app-commandbar" />
        <ConfigurationFormComponent t={this.props.t} ref={this.configure} />
        <PreferencesFormComponent ref={this.preferences}
          toggleHighContrastMode={this.props.toggleHighContrastMode}
          toggleOptionalComponent={this.props.toggleOptionalComponent}
          optionalComponentsState={this.props.optionalComponentsState}
          contrast={this.props.contrast}
          t={this.props.t} />
        <DeleteAllTwinsComponent ref={this.delete} t={this.props.t} />
        <KeyboardShortcutsComponents ref={this.keyboard} t={this.props.t} />
      </div>
    );
  }

}

export default withTranslation()(AppCommandBar);
